import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Button,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import { ExploreStatsIcon } from "../../layout/sidebar/icons/ExploreStatsIcon";
import { PasswordCheckIcon } from "../../layout/sidebar/icons/PasswordCheckIcon";
import { BarcodeIcon } from "../../layout/sidebar/icons/BarcodeIcon";
import { ClockIcon } from "../../layout/sidebar/icons/ClockIcon";
import { SpeedometerIcon } from "../../layout/sidebar/icons/SpeedometerIcon";
import { MirrorIcon } from "../../layout/sidebar/icons/MirrorIcon";
import { CalendarIcon } from "../../layout/sidebar/icons/CalendarIcon";
import { getStatusStyles } from "./utils/statusStyles";
import {
  mockAutologStats,
  mockEmployees,
  mockLeaderboard,
} from "./mockData/data";
import { AutologStats, Employee, LeaderboardEntry } from "./types/types";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  iconBg: string;
}

const AutoLogPage: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [stats] = useState<AutologStats>(mockAutologStats);
  const [employees] = useState<Employee[]>(mockEmployees);
  const [leaderboard] = useState<LeaderboardEntry[]>(mockLeaderboard);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [openPicker, setOpenPicker] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);

  const handleEmployeeClick = (employeeId: number) => {
    navigate(`/autolog/${employeeId}`);
  };

  const formatHeaderLabel = (date: Dayjs | null) => {
    if (!date) return "Today";
    if (date.isSame(dayjs(), "day")) return "Today";
    return date.format("D MMMM");
  };

  const formatChipLabel = (date: Dayjs | null) => {
    if (!date) return dayjs().format("D MMMM");
    return date.format("D MMMM");
  };

  const StatCard = ({ icon, title, value, iconBg }: StatCardProps) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${colors.primary[300]}`,
        background: colors.primary[900],
        minHeight: { xs: 120, sm: 140 },
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          border: "1px solid #2196F3",
          boxShadow: "0 4px 12px rgba(33, 150, 243, 0.15)",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={2}
      >
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontSize: { xs: "0.75rem", sm: "0.875rem" }, mt: 2 }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            width: { xs: 36, sm: 42 },
            height: { xs: 36, sm: 42 },
            borderRadius: 50,
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& svg": {
              width: { xs: "20px", sm: "24px" },
              height: { xs: "20px", sm: "24px" },
            },
          }}
        >
          {icon}
        </Box>
      </Box>

      <Typography
        variant="h3"
        fontWeight="bold"
        sx={{
          fontSize: { xs: 24, sm: 32 },
          fontWeight: 900,
          mt: "auto",
          pt: 1,
          pb: 2,
        }}
      >
        {value}
      </Typography>
    </Paper>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 0, minHeight: "100vh" }}>
        {/* Stats Cards */}
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(auto-fit, minmax(150px, 1fr))",
            sm: "repeat(auto-fit, minmax(180px, 1fr))",
            md: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
          gap={{ xs: 1, sm: 1 }}
          mb={3}
        >
          <StatCard
            icon={<MirrorIcon />}
            title="Average P-Score"
            value={stats.averagePScore.toString()}
            iconBg="#E3F2FD"
          />
          <StatCard
            icon={<ClockIcon />}
            title="On-time Rate"
            value={`${stats.onTimeRate}%`}
            iconBg="#E3F2FD"
          />
          <StatCard
            icon={<SpeedometerIcon />}
            title="Turn Up %"
            value={`${stats.turnUpPercentage}%`}
            iconBg="#FFF3E0"
          />
          <Paper
            elevation={0}
            sx={{
              px: { xs: 2, sm: 3 },
              pb: 2,
              pt: 2,
              borderRadius: 3,
              border: "2px solid #2196F3",
              background: colors.primary[900],
              display: "flex",
              flexDirection: "column",
              minHeight: { xs: 120, sm: 140 },
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 4px 12px rgba(33, 150, 243, 0.25)",
                borderColor: "#1976D2",
              },
            }}
          >
            <Box
              sx={{
                py: 1,
                "& svg": {
                  width: { xs: "48px !important", sm: "64px !important" },
                  height: { xs: "48px !important", sm: "64px !important" },
                },
              }}
            >
              <ExploreStatsIcon />
            </Box>
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                py: 1,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                backgroundColor: "#2196F3",
                "&:hover": {
                  backgroundColor: "#1976D2",
                },
              }}
            >
              Explore Stats
            </Button>
          </Paper>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", lg: "2fr 1fr" }}
          gap={{ xs: 2, sm: 3 }}
        >
          {/* Autolog Table */}
          <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              p={{ xs: 2, sm: 3 }}
              pb={2}
              gap={{ xs: 1, sm: 0 }}
            >
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                {formatHeaderLabel(selectedDate)} Autolog
              </Typography>
              <Chip
                ref={chipRef}
                icon={<CalendarIcon />}
                label={formatChipLabel(selectedDate)}
                onClick={() => setOpenPicker(true)}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  cursor: "pointer",
                  backgroundColor: colors.primary[800],
                  "&:hover": {
                    backgroundColor: "#e3e9ef",
                  },
                }}
              />
              <DatePicker
                open={openPicker}
                onClose={() => setOpenPicker(false)}
                value={selectedDate}
                disableFuture
                onChange={(newValue) => {
                  if (newValue === null) {
                    setSelectedDate(null);
                  } else if (newValue instanceof Date) {
                    setSelectedDate(dayjs(newValue));
                  } else {
                    setSelectedDate(newValue);
                  }
                  setOpenPicker(false);
                }}
                slotProps={{
                  popper: {
                    placement: "bottom",
                    anchorEl: chipRef.current,
                  },
                  textField: {
                    sx: { display: "none" },
                  },
                }}
              />
            </Box>
            <TableContainer sx={{ px: { xs: 1, sm: 2 }, overflowX: "auto" }}>
              <Table
                sx={{
                  minWidth: { xs: 600, sm: "auto" },
                  "& .MuiTableCell-root": {
                    border: 0,
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      background: colors.primary[800],
                      "& .MuiTableCell-root": {
                        border: 0,
                        py: { xs: 1, sm: 1.5 },
                      },
                      "& .MuiTableCell-root:first-of-type": {
                        borderTopLeftRadius: "12px",
                        borderBottomLeftRadius: "12px",
                        paddingLeft: { xs: 2, sm: 3 },
                      },
                      "& .MuiTableCell-root:last-of-type": {
                        borderTopRightRadius: "12px",
                        borderBottomRightRadius: "12px",
                        paddingRight: { xs: 2, sm: 3 },
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>
                      Employee Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Current Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Arrival Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Manual Check-in
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => {
                    const statusStyles = getStatusStyles(employee.status);
                    return (
                      <TableRow
                        key={employee.id}
                        hover
                        onClick={() => handleEmployeeClick(employee.id)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "#F5F9FF",
                          },
                        }}
                      >
                        <TableCell>{employee.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={employee.status}
                            sx={{
                              background: statusStyles.statusColor,
                              color: statusStyles.statusTextColor,
                              border: statusStyles.border,
                              fontWeight: 500,
                              borderRadius: 50,
                              px: { xs: 1, sm: 1.5 },
                              py: 0.2,
                              maxHeight: "24px",
                              fontSize: { xs: "0.7rem", sm: "12px" },
                              "& .MuiChip-label": {
                                px: 0,
                                py: 0,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>{employee.arrivalTime}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <BarcodeIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <PasswordCheckIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Arrival Leaderboard */}
          <Paper elevation={0} sx={{ borderRadius: 3, p: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h6"
              fontWeight="600"
              mb={2}
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              Arrival Leaderboard
            </Typography>
            <Chip
              label="Stats are based on punctuality score"
              size="small"
              sx={{
                background: "#E3F2FD",
                color: "#2196F3",
                mb: 2,
                fontWeight: 500,
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            />

            {/* Top 3 */}
            <Box
              display="flex"
              justifyContent="space-around"
              alignItems="flex-end"
              mb={2}
              flexWrap={{ xs: "nowrap", sm: "nowrap" }}
            >
              {/* 2nd Place */}
              <Box textAlign="center">
                <Box position="relative" display="inline-block">
                  <Avatar
                    src={leaderboard[0].avatarUrl}
                    alt={leaderboard[0].name}
                    sx={{
                      width: { xs: 48, sm: 64 },
                      height: { xs: 48, sm: 64 },
                      border: "3px solid #F2964C",
                      mb: 1,
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                    }}
                  >
                    {leaderboard[0].avatar}
                  </Avatar>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: { xs: 8, sm: 12 },
                      left: -2,
                      width: { xs: 18, sm: 21 },
                      height: { xs: 18, sm: 21 },
                      borderRadius: "50%",
                      background: "#F2964C",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: { xs: "10px", sm: "12px" },
                    }}
                  >
                    2
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                >
                  {leaderboard[0].name}
                </Typography>
                <Chip
                  label={leaderboard[0].score}
                  size="small"
                  sx={{
                    background: "#FFF3E0",
                    color: "#F2964C",
                    mt: 0.5,
                    border: "1px solid #F2964C",
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  }}
                />
              </Box>

              {/* 1st Place */}
              <Box
                textAlign="center"
                sx={{
                  transform: {
                    xs: "translateY(-10px)",
                    sm: "translateY(-20px)",
                  },
                  mt: 2,
                }}
              >
                <Box position="relative" display="inline-block">
                  <Avatar
                    src={leaderboard[1].avatarUrl}
                    alt={leaderboard[1].name}
                    sx={{
                      width: { xs: 56, sm: 72 },
                      height: { xs: 56, sm: 72 },
                      border: "3px solid #4BA663",
                      mb: 1,
                      fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    }}
                  >
                    {leaderboard[1].avatar}
                  </Avatar>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: { xs: 8, sm: 10 },
                      left: -2,
                      width: { xs: 20, sm: 24 },
                      height: { xs: 20, sm: 24 },
                      borderRadius: "50%",
                      background: "#4BA663",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: { xs: "10px", sm: "12px" },
                    }}
                  >
                    1
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                >
                  {leaderboard[1].name}
                </Typography>
                <Chip
                  label={leaderboard[1].score}
                  size="small"
                  sx={{
                    background: "#E8F5E9",
                    color: "#4BA663",
                    mt: 0.5,
                    py: 0.1,
                    padding: "1px",
                    border: "1px solid #4BA663",
                    fontSize: { xs: "0.65rem", sm: "10px" },
                  }}
                />
              </Box>

              {/* 3rd Place */}
              <Box textAlign="center">
                <Box position="relative" display="inline-block">
                  <Avatar
                    src={leaderboard[2].avatarUrl}
                    alt={leaderboard[2].name}
                    sx={{
                      width: { xs: 48, sm: 64 },
                      height: { xs: 48, sm: 64 },
                      border: "3px solid #C59A70",
                      mb: 1,
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                    }}
                  >
                    {leaderboard[2].avatar}
                  </Avatar>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: { xs: 8, sm: 12 },
                      left: -2,
                      width: { xs: 18, sm: 21 },
                      height: { xs: 18, sm: 21 },
                      borderRadius: "50%",
                      background: "#C59A70",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: { xs: "10px", sm: "12px" },
                    }}
                  >
                    3
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                >
                  {leaderboard[2].name}
                </Typography>
                <Chip
                  label={leaderboard[2].score}
                  size="small"
                  sx={{
                    background: "#FFF3E0",
                    color: "#A1764B",
                    mt: 0.5,
                    border: "1px solid #A1764B",
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  }}
                />
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="center"
              mb={2}
              padding={0.2}
              sx={{ background: colors.primary[800], borderRadius: 2 }}
            >
              <Typography color="text.secondary">• • •</Typography>
            </Box>

            {/* Rest of leaderboard */}
            {leaderboard.slice(3).map((entry) => (
              <Box
                key={entry.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={{ xs: 1.5, sm: 2 }}
                mb={1}
                sx={{ background: colors.primary[800], borderRadius: 2 }}
              >
                <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight="600"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                  >
                    #{entry.rank}
                  </Typography>
                  <Avatar
                    src={entry.avatarUrl}
                    alt={entry.name}
                    sx={{
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                      background: "#5C6BC0",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    {entry.avatar}
                  </Avatar>
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                  >
                    {entry.name}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                >
                  {entry.score}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AutoLogPage;
